/**
 * runSearch — runs a host's `searchable` kinds + cross-app
 * federation in parallel against a single query, with per-target
 * abort-on-timeout and per-target try/catch. Returns a sectional
 * result object that EmptyBody can render directly.
 *
 * Per plan-embed-fixes-11-per-app-search.md.
 */

import type { AgentEmbedConfig, SearchableHit, SearchableKind } from '../types'

export interface SearchSection {
  /** kind for in-app sections, '<app>:<kind>' for cross-app */
  id: string
  /** UPPERCASE section label */
  label: string
  /** Whether clicks should open in a new tab. In-host: usually
   *  no. Cross-app: always yes. */
  newTab: boolean
  hits: SearchableHit[]
}

export interface SearchRun {
  /** Host's own searchable-kind sections. Render ABOVE the
   *  "In other apps" divider, in config order. */
  hostSections: SearchSection[]
  /** Cross-app sections. Render BELOW the divider. */
  crossAppSections: SearchSection[]
  /** Number of cross-app targets that timed out or errored.
   *  Coalesced into a single muted line at the bottom of the
   *  palette so the user knows results may be incomplete. */
  failedCount: number
}

const DEFAULT_TIMEOUT_MS = 1500
const DEFAULT_MAX_PER_KIND = 5

interface RunOpts {
  /** Override the per-kind timeout (defaults to config or 1500). */
  timeoutMs?: number
}

export async function runSearch(query: string, config: AgentEmbedConfig, opts: RunOpts = {}): Promise<SearchRun> {
  const trimmed = query.trim()
  if (trimmed.length === 0) {
    return { hostSections: [], crossAppSections: [], failedCount: 0 }
  }

  const timeoutMs = opts.timeoutMs ?? config.crossApp?.timeoutMs ?? DEFAULT_TIMEOUT_MS

  // ── Host kinds: run all in parallel ─────────────────────────
  const hostPromises = config.searchable.map(async (kind): Promise<SearchSection | null> => {
    const hits = await runOneKind(kind, trimmed, timeoutMs)
    if (hits.length === 0) {
      return null
    }
    return {
      id: kind.kind,
      label: kind.label,
      newTab: kind.target === '_blank',
      hits: hits.slice(0, kind.max ?? DEFAULT_MAX_PER_KIND),
    }
  })

  // ── Cross-app targets: run all in parallel ──────────────────
  const crossPromises = (config.crossApp?.apps ?? []).map(
    async (target): Promise<{ section: SearchSection | null; failed: boolean }> => {
      const ctrl = new AbortController()
      const timer = window.setTimeout(() => ctrl.abort(), timeoutMs)
      try {
        const hits = await target.search(trimmed, ctrl.signal)
        if (hits.length === 0) {
          return { section: null, failed: false }
        }
        return {
          section: {
            id: `${target.app}:${target.kindLabel ?? 'results'}`,
            label: target.kindLabel ?? target.app.toUpperCase(),
            newTab: true, // cross-app always opens new tab
            hits: hits.slice(0, DEFAULT_MAX_PER_KIND),
          },
          failed: false,
        }
      } catch {
        // timeout or per-target error — coalesce into failedCount
        return { section: null, failed: true }
      } finally {
        window.clearTimeout(timer)
      }
    },
  )

  const [hostResolved, crossResolved] = await Promise.all([Promise.all(hostPromises), Promise.all(crossPromises)])

  return {
    hostSections: hostResolved.filter((s): s is SearchSection => s !== null),
    crossAppSections: crossResolved.map((r) => r.section).filter((s): s is SearchSection => s !== null),
    failedCount: crossResolved.filter((r) => r.failed).length,
  }
}

async function runOneKind(kind: SearchableKind, query: string, timeoutMs: number): Promise<SearchableHit[]> {
  const ctrl = new AbortController()
  const timer = window.setTimeout(() => ctrl.abort(), timeoutMs)
  try {
    return await kind.search(query, ctrl.signal)
  } catch {
    // host kinds fail silently — empty section just doesn't render
    return []
  } finally {
    window.clearTimeout(timer)
  }
}
